import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimes, FaExclamationCircle } from 'react-icons/fa';

// Toast Context
const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const toast = {
    success: (message, duration) => addToast(message, 'success', duration),
    error: (message, duration) => addToast(message, 'error', duration || 5000),
    warning: (message, duration) => addToast(message, 'warning', duration),
    info: (message, duration) => addToast(message, 'info', duration),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// Individual Toast Component - Glassmorphism Style
const Toast = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const startTime = Date.now();
    const duration = toast.duration;

    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
    }, 50);

    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onRemove(toast.id), 300);
    }, duration);

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
  }, [toast, onRemove]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onRemove(toast.id), 300);
  };

  // Glassmorphism color config
  const config = {
    success: {
      icon: FaCheckCircle,
      iconBg: 'rgba(13, 148, 136, 0.15)',
      iconColor: '#0d9488',
      progressBg: '#0d9488',
      borderColor: 'rgba(13, 148, 136, 0.2)'
    },
    error: {
      icon: FaExclamationCircle,
      iconBg: 'rgba(220, 38, 38, 0.15)',
      iconColor: '#dc2626',
      progressBg: '#dc2626',
      borderColor: 'rgba(220, 38, 38, 0.2)'
    },
    warning: {
      icon: FaExclamationTriangle,
      iconBg: 'rgba(217, 119, 6, 0.15)',
      iconColor: '#d97706',
      progressBg: '#d97706',
      borderColor: 'rgba(217, 119, 6, 0.2)'
    },
    info: {
      icon: FaInfoCircle,
      iconBg: 'rgba(100, 116, 139, 0.15)',
      iconColor: '#64748b',
      progressBg: '#64748b',
      borderColor: 'rgba(100, 116, 139, 0.2)'
    }
  };

  const { icon: Icon, iconBg, iconColor, progressBg, borderColor } = config[toast.type] || config.info;

  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.75)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `1px solid ${borderColor}`,
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        minWidth: '280px',
        maxWidth: '360px',
        overflow: 'hidden',
        transform: isExiting ? 'translateX(120%)' : 'translateX(0)',
        opacity: isExiting ? 0 : 1,
        transition: 'all 0.3s ease',
        animation: 'slideIn 0.3s ease'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '14px 16px 10px 16px'
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          background: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Icon style={{ fontSize: '18px', color: iconColor }} />
        </div>
        <div style={{ 
          flex: 1, 
          fontSize: '0.9rem', 
          fontWeight: '500', 
          lineHeight: '1.4',
          color: '#1f2937'
        }}>
          {toast.message}
        </div>
        <button
          onClick={handleClose}
          style={{
            background: 'rgba(0,0,0,0.05)',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0,0,0,0.1)';
            e.currentTarget.style.color = '#6b7280';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0,0,0,0.05)';
            e.currentTarget.style.color = '#9ca3af';
          }}
        >
          <FaTimes style={{ fontSize: '12px' }} />
        </button>
      </div>
      
      {/* Progress bar */}
      <div style={{
        height: '3px',
        background: 'rgba(0,0,0,0.05)',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: progressBg,
          transition: 'width 0.1s linear',
          opacity: 0.7
        }} />
      </div>
    </div>
  );
};

// Toast Container
const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(120%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          @keyframes successPulse {
            0% { transform: scale(0); opacity: 0; }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes checkDraw {
            0% { stroke-dashoffset: 100; }
            100% { stroke-dashoffset: 0; }
          }
          @keyframes circleDraw {
            0% { stroke-dashoffset: 314; }
            100% { stroke-dashoffset: 0; }
          }
        `}
      </style>
      <div
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          pointerEvents: 'none'
        }}
      >
        {toasts.map(toast => (
          <div key={toast.id} style={{ pointerEvents: 'auto' }}>
            <Toast toast={toast} onRemove={removeToast} />
          </div>
        ))}
      </div>
    </>
  );
};

// Success Animation Component (standalone) - Glassmorphism Style
export const SuccessAnimation = ({ show, onComplete, message }) => {
  useEffect(() => {
    if (show && onComplete) {
      const timer = setTimeout(onComplete, 2000);
      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        animation: 'fadeIn 0.3s ease'
      }}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}
      </style>
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '40px 50px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          border: '1px solid rgba(255,255,255,0.5)'
        }}
      >
        <svg
          width="80"
          height="80"
          viewBox="0 0 100 100"
          style={{ animation: 'successPulse 0.5s ease' }}
        >
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="#0d9488"
            strokeWidth="5"
            strokeDasharray="314"
            strokeDashoffset="314"
            style={{
              animation: 'circleDraw 0.6s ease forwards'
            }}
          />
          <path
            d="M30 50 L45 65 L70 35"
            fill="none"
            stroke="#0d9488"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="100"
            strokeDashoffset="100"
            style={{
              animation: 'checkDraw 0.4s ease 0.4s forwards'
            }}
          />
        </svg>
        {message && (
          <div style={{
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#1f2937',
            textAlign: 'center',
            maxWidth: '280px',
            lineHeight: '1.5'
          }}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToastProvider;
